import React, { useEffect, useMemo, useState } from 'react'
import { Card, Col, Progress, Row, Statistic, Typography, Alert, Calendar, List, Tag } from 'antd'
import { PieChart, BarChart, GridlineSeries, Gridline } from 'reaviz'
import adminService from '../../../services/admin'

const { Title, Text } = Typography

const toPieData = (obj) => {
  if (!obj) return []
  return Object.keys(obj).map((k) => ({ key: k, data: obj[k] }))
}

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const res = await adminService.getOverview()
        if (!mounted) return
        setData(res)
        setError(null)
      } catch (e) {
        if (!mounted) return
        setError(e?.response?.data?.message || e?.message || 'Failed to load admin analytics')
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const calendarItemsForDay = useMemo(() => {
    if (!data?.calendar || !selectedDay) return []
    const key = selectedDay
    return data.calendar[key]?.items || []
  }, [data, selectedDay])

  const box = data?.boxplot
  const boxSeries = useMemo(() => {
    if (!box || box.count === 0) return []
    // Reaviz doesn't ship a native BoxPlot in this project; we show the 5-number summary as a bar chart.
    return [
      { key: 'min', data: box.min },
      { key: 'q1', data: box.q1 },
      { key: 'median', data: box.median },
      { key: 'q3', data: box.q3 },
      { key: 'max', data: box.max }
    ]
  }, [box])

  if (loading) return <div style={{ padding: 12 }}>Loading…</div>

  return (
    <>
      <Title level={3}>Admin Dashboard</Title>
      <Text type="secondary">
        System-wide analytics and management. Data is computed from MongoDB collections (no dummy).
      </Text>

      {error && (
        <div style={{ marginTop: 12 }}>
          <Alert type="error" message={error} />
        </div>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Users" value={data?.counts?.usersTotal || 0} />
            <Text type="secondary">Active: {data?.counts?.usersActive || 0}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Courses" value={data?.counts?.coursesTotal || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Assessments" value={data?.counts?.assessmentsTotal || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Submissions" value={data?.counts?.submissionsTotal || 0} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <Card title="Users by role (pie)">
            <PieChart width={340} height={260} data={toPieData(data?.roles)} />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Courses by status (pie)">
            <PieChart width={340} height={260} data={toPieData(data?.coursesByStatus)} />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Progress">
            <Text>{data?.progress?.label}</Text>
            <div style={{ marginTop: 12 }}>
              <Progress percent={data?.progress?.percent || 0} />
              <Text type="secondary">
                {data?.progress?.numerator || 0} / {data?.progress?.denominator || 0}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Achievement score distribution (boxplot summary)">
            {box?.count ? (
              <>
                <BarChart
                  width={520}
                  height={260}
                  data={boxSeries}
                  gridlines={<GridlineSeries line={<Gridline direction="y" />} />}
                />
                <Text type="secondary">n = {box.count}</Text>
              </>
            ) : (
              <Text type="secondary">No achievements yet.</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Calendar analysis (assessments open/close/due)"
            extra={<Text type="secondary">{selectedDay || 'Select a day'}</Text>}
          >
            <Calendar
              fullscreen={false}
              onSelect={(momentLike) => {
                // Antd v4 uses moment; v5 uses dayjs. We'll support both by using toDate if present.
                const d = momentLike?.toDate ? momentLike.toDate() : new Date(momentLike)
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
                  d.getDate()
                ).padStart(2, '0')}`
                setSelectedDay(key)
              }}
              dateCellRender={(momentLike) => {
                const d = momentLike?.toDate ? momentLike.toDate() : new Date(momentLike)
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
                  d.getDate()
                ).padStart(2, '0')}`
                const cell = data?.calendar?.[key]
                if (!cell || !cell.total) return null
                return (
                  <div style={{ marginTop: 6 }}>
                    <Tag color="blue">Total {cell.total}</Tag>
                    {cell.due ? <Tag color="orange">Due {cell.due}</Tag> : null}
                    {cell.open ? <Tag color="green">Open {cell.open}</Tag> : null}
                    {cell.close ? <Tag color="red">Close {cell.close}</Tag> : null}
                  </div>
                )
              }}
            />

            <div style={{ marginTop: 12 }}>
              <List
                size="small"
                bordered
                locale={{ emptyText: 'No items for selected day' }}
                dataSource={calendarItemsForDay}
                renderItem={(it) => (
                  <List.Item>
                    <Text strong style={{ marginRight: 8 }}>
                      {it.kind.toUpperCase()}
                    </Text>
                    <Text>{it.title}</Text>
                    <Text type="secondary" style={{ marginLeft: 'auto' }}>
                      {it.course?.name || ''}
                    </Text>
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default AdminDashboard

